import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import { FileText, Plus, X, CheckCircle2, Trash2, Edit2 } from 'lucide-react'
import { getExaminationTemplates, saveExaminationTemplate, deleteExaminationTemplate } from '../services/examinationTemplates'

const ExaminationTemplateSelector = ({ onSelectTemplate, selectedTemplateId }) => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (organizationId) {
      fetchTemplates()
    }
  }, [organizationId])

  const fetchTemplates = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const result = await getExaminationTemplates(organizationId)
      if (result.success) {
        setTemplates(result.data || [])
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTemplate = async () => {
    if (!templateName.trim() || !organizationId) return
    try {
      setSaving(true)
      // Get current form data from parent (would need to be passed as prop)
      // For now, we'll create a basic template structure
      await saveExaminationTemplate(organizationId, {
        name: templateName.trim(),
        type: 'charting', // Default type for full examination templates
        description: templateDescription.trim(),
        templateData: {}, // Would be populated from current form sections
      })
      setTemplateName('')
      setTemplateDescription('')
      setShowCreateForm(false)
      fetchTemplates()
    } catch (error) {
      console.error('Error creating template:', error)
      alert(error.message || 'Failed to create template')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return
    try {
      await deleteExaminationTemplate(templateId)
      fetchTemplates()
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading templates...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText size={20} />
          Examination Templates
        </h3>
        {canWrite('examinations') && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            New Template
          </Button>
        )}
      </div>

      {/* Create Template Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">Create Template from Current Form</h4>
                <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-3">
                <Input
                  label="Template Name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Standard Intake, Follow-up Exam"
                  required
                />
                <Textarea
                  label="Description"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Describe when to use this template..."
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button onClick={handleCreateTemplate} disabled={!templateName.trim() || saving} className="flex-1">
                    {saving ? 'Saving...' : 'Save Template'}
                  </Button>
                  <Button variant="secondary" onClick={() => setShowCreateForm(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <FileText size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No templates created yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((template) => (
            <Card
              key={template.id}
              variant="soft"
              hover
              className={`p-4 cursor-pointer border-2 transition-all ${
                selectedTemplateId === template.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-transparent hover:border-primary-200'
              }`}
              onClick={() => onSelectTemplate && onSelectTemplate(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    {template.is_default && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700">
                        Default
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Created {new Date(template.created_at).toLocaleDateString()}
                  </p>
                </div>
                {canWrite('examinations') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteTemplate(template.id)
                    }}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {selectedTemplateId === template.id && (
                <div className="mt-2 pt-2 border-t border-primary-200">
                  <span className="text-xs text-primary-600 font-semibold flex items-center gap-1">
                    <CheckCircle2 size={12} />
                    Selected
                  </span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default ExaminationTemplateSelector

